/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Ortak konuşma amacı ve sürümlü AI istemci sözleşmesi. Tek kaynak Core'dur.
 */
export type AssistantConversationPurpose = "BUSINESS" | "QUBEPRO_HELP";
export function readAssistantPurpose(
  value: unknown,
): AssistantConversationPurpose | null {
  return value === "BUSINESS" || value === "QUBEPRO_HELP" ? value : null;
}

/** Always use the versioned handler; a legacy node must never ignore the purpose. */
export function assistantPurposePath(
  path: string,
  purpose: AssistantConversationPurpose,
): string {
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/v2/"))
    throw new Error("Invalid assistant endpoint");
  const [pathname, query = ""] = path.split("?");
  const params = new URLSearchParams(query);
  params.set("purpose", purpose);
  return `/v2${pathname}?${params.toString()}`;
}

/** Only explicit help text and canonical identity cross the help boundary. */
export function buildAssistantPurposeChatPayload(
  purpose: AssistantConversationPurpose,
  input: Record<string, unknown>,
) {
  if (purpose === "BUSINESS") return { ...input, purpose };
  return {
    purpose,
    message: input.message,
    conversationId: input.conversationId,
    assistantId: input.assistantId,
    language: input.language,
  };
}

const COPY = {
  tr: [
    "Konuşma amacı",
    "Şirket işi",
    "QubePro yardım",
    "Amacı değiştirmek yeni bir konuşma açar.",
    "Kullanım ve destek soruları. Şirket verileri ve çalışan araçları kullanılmaz.",
    "QubePro ile ilgili sorunuzu yazın...",
    "Bu konuşma türü şu anda kullanılamıyor.",
  ],
  en: [
    "Conversation purpose",
    "Company work",
    "QubePro help",
    "Changing purpose starts a new conversation.",
    "Usage and support questions. Company data and worker tools are not used.",
    "Ask a question about QubePro...",
    "This conversation type is currently unavailable.",
  ],
  ar: [
    "غرض المحادثة",
    "أعمال الشركة",
    "مساعدة QubePro",
    "يبدأ تغيير الغرض محادثة جديدة.",
    "أسئلة الاستخدام والدعم. لا تُستخدم بيانات الشركة أو أدوات الموظفين.",
    "اكتب سؤالك عن QubePro...",
    "نوع المحادثة هذا غير متاح حاليًا.",
  ],
  de: [
    "Gesprächszweck",
    "Firmenaufgaben",
    "QubePro Hilfe",
    "Ein anderer Zweck startet ein neues Gespräch.",
    "Fragen zu Nutzung und Support. Firmendaten und Mitarbeiterwerkzeuge werden nicht verwendet.",
    "Stellen Sie eine Frage zu QubePro...",
    "Dieser Gesprächstyp ist derzeit nicht verfügbar.",
  ],
  fr: [
    "Objet de la conversation",
    "Travail entreprise",
    "Aide QubePro",
    "Changer d’objet ouvre une nouvelle conversation.",
    "Questions d’utilisation et de support. Les données et outils de l’entreprise ne sont pas utilisés.",
    "Posez une question sur QubePro...",
    "Ce type de conversation est actuellement indisponible.",
  ],
  es: [
    "Propósito de la conversación",
    "Trabajo de empresa",
    "Ayuda QubePro",
    "Cambiar el propósito inicia una conversación nueva.",
    "Preguntas de uso y soporte. No se utilizan datos de empresa ni herramientas de empleados.",
    "Haga una pregunta sobre QubePro...",
    "Este tipo de conversación no está disponible actualmente.",
  ],
  it: [
    "Scopo della conversazione",
    "Lavoro aziendale",
    "Aiuto QubePro",
    "Cambiare scopo apre una nuova conversazione.",
    "Domande su uso e supporto. Dati aziendali e strumenti dei dipendenti non vengono usati.",
    "Fai una domanda su QubePro...",
    "Questo tipo di conversazione non è al momento disponibile.",
  ],
  ru: [
    "Цель беседы",
    "Работа компании",
    "Помощь QubePro",
    "Смена цели начинает новую беседу.",
    "Вопросы об использовании и поддержке. Данные компании и инструменты сотрудников не используются.",
    "Задайте вопрос о QubePro...",
    "Этот тип беседы сейчас недоступен.",
  ],
  zh: [
    "对话目的",
    "公司工作",
    "QubePro 帮助",
    "更改目的会开始新的对话。",
    "使用与支持问题。不会使用公司数据或员工工具。",
    "请输入有关 QubePro 的问题...",
    "此类对话目前不可用。",
  ],
  ja: [
    "会話の目的",
    "会社の業務",
    "QubePro ヘルプ",
    "目的を変更すると新しい会話が始まります。",
    "使い方とサポートの質問。会社のデータや従業員ツールは使用しません。",
    "QubePro について質問してください...",
    "この種類の会話は現在利用できません。",
  ],
} as const;

export function assistantPurposeCopy(language = "tr") {
  const key = language.toLowerCase().split("-")[0] as keyof typeof COPY;
  const [label, business, help, hint, helpHint, placeholder, unavailable] =
    COPY[key] ?? COPY.en;
  return { label, business, help, hint, helpHint, placeholder, unavailable };
}
export function assertAssistantResponsePurpose(
  data: Record<string, unknown>,
  purpose: AssistantConversationPurpose,
  language?: string,
): void {
  if (readAssistantPurpose(data.purpose) !== purpose)
    throw new Error(assistantPurposeCopy(language).unavailable);
}
